// src/components/ProfessionalServicesConfig.tsx
// Servicios del profesional: la lista y el formulario de cada uno.
//
// Lavada de cara oct-2026 con el diseño del asistente de primeros pasos
// (MarcaUI / Marca.css). Además:
//  - la tarjeta y el formulario eran componentes definidos ADENTRO de este:
//    cada vez que la pantalla se volvía a dibujar (por ejemplo, cuando se iba
//    el cartel de "Servicio creado") se montaban de nuevo. El formulario
//    perdía lo que se estaba escribiendo y las tarjetas repetían la entrada.
//    Ahora son componentes aparte (TarjetaServicio, FormularioServicio);
//  - el precio va con puntos y se lee bien: "25.000" en el input numérico de
//    antes quedaba en 25.

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { apiService, Service, ServiceScheduleWindow, PriceMode } from '../services/api';
import { displayPrice } from '../utils/priceDisplay';
import LoadingSpinner from './LoadingSpinner';
import {
  Plus, Pencil, Trash2, Clock, X, CheckCircle, Eye, EyeOff, AlertTriangle,
  Stethoscope, Sparkles, FileText, ChevronDown, ChevronUp
} from 'lucide-react';
import { PortadaMarca, GrupoDeFichas, OpcionConInterruptor } from './MarcaUI';

// Un día del horario de atención, como lo devuelve getScheduleConfig
type DiaDeAtencion = { day: string; isWorking: boolean; startTime: string; endTime: string };

interface User {
  id: number;
  nombre: string;
  apellido: string;
  es_profesional: boolean;
  professional_type: 'salud' | 'belleza';
  especialidad: string;
}

interface ServiceFormData {
  id?: number;
  name: string;
  description: string;
  duration: number;
  price: number;
  category: string;
  requires_consultation: boolean;
  before_care_instructions: string;
  after_care_instructions: string;
  is_active: boolean;
  price_mode: PriceMode;
}

// Categorías predefinidas. Ambos tipos incluyen etiquetas de bienestar
// (pedido de Flavia Priori: reflexología/stretching no encajaban en las de
// belleza) y un "Otro" genérico como escape para cualquier rubro.
const CATEGORIAS: Record<'salud' | 'belleza', string[]> = {
  belleza: [
    'Pestañas', 'Cejas', 'Uñas', 'Cabello', 'Depilación', 'Estética Facial', 'Maquillaje',
    'Masajes', 'Reflexología', 'Bienestar Corporal', 'Otro'
  ],
  salud: [
    'Consulta', 'Urgencia', 'Pediatría', 'Psicología', 'Control', 'Especialidad', 'Terapia',
    'Masajes', 'Reflexología', 'Bienestar Corporal', 'Otro'
  ]
};

const DURACIONES = [15, 20, 30, 40, 45, 50, 60, 90, 120];

const FORMULARIO_VACIO: ServiceFormData = {
  name: '',
  description: '',
  duration: 30,
  price: 0,
  category: '',
  requires_consultation: false,
  before_care_instructions: '',
  after_care_instructions: '',
  is_active: true,
  price_mode: 'fijo'
};

const formatDuration = (mins: number) => {
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const remaining = mins % 60;
  return remaining > 0 ? `${hours} h ${remaining}` : `${hours} h`;
};

// "25.000" a mano: toLocaleString depende del navegador
const conPuntos = (n: number) => (n > 0 ? String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.') : '');

const dayLabels = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// ─────────────────────────────────────
// TARJETA DE UN SERVICIO
// ─────────────────────────────────────
interface TarjetaServicioProps {
  service: Service;
  tipo: 'salud' | 'belleza';
  /** Escalona la entrada: cada tarjeta arranca 55 ms después de la anterior */
  orden: number;
  borrando: boolean;
  onEditar: () => void;
  onPedirBorrar: () => void;
  onBorrar: () => void;
  onCancelarBorrar: () => void;
}

const TarjetaServicio: React.FC<TarjetaServicioProps> = ({ service, tipo, orden, borrando, onEditar, onPedirBorrar, onBorrar, onCancelarBorrar }) => {
  const oculto = !service.is_active;
  return (
    <div
      className={`relative rounded-3xl border border-gray-200 bg-white p-4 shadow-sm flex flex-col gap-3 animate-entrada-abajo ${oculto ? 'opacity-60' : ''}`}
      style={{ animationDelay: `${Math.min(orden, 8) * 55}ms` }}
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl border marca-tarjeta marca-acento flex items-center justify-center shrink-0">
          {tipo === 'belleza' ? <Sparkles className="w-5 h-5" /> : <Stethoscope className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-gray-900 leading-tight">{service.name}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{service.category} · {formatDuration(service.duration)}</p>
        </div>
        <p className="font-bold text-gray-900 text-right shrink-0">{displayPrice(service)}</p>
      </div>

      {service.description && (
        <p className="text-sm text-gray-500 line-clamp-2">{service.description}</p>
      )}

      {(oculto || service.has_custom_schedule || service.requires_consultation || service.before_care_instructions || service.after_care_instructions) && (
        <div className="flex flex-wrap gap-1.5">
          {oculto && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 text-[11px] font-semibold">
              <EyeOff className="w-3 h-3" /> Oculto
            </span>
          )}
          {service.has_custom_schedule && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border marca-tarjeta marca-acento text-[11px] font-medium">
              <Clock className="w-3 h-3" /> Horario propio
            </span>
          )}
          {service.requires_consultation && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 text-[11px] font-medium border border-amber-200">
              <Stethoscope className="w-3 h-3" /> Requiere consulta
            </span>
          )}
          {(service.before_care_instructions || service.after_care_instructions) && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-50 text-gray-600 text-[11px] font-medium border border-gray-200">
              <FileText className="w-3 h-3" /> Con indicaciones
            </span>
          )}
        </div>
      )}

      {borrando ? (
        <div className="flex items-center gap-2 rounded-2xl bg-red-50 border border-red-100 px-3 py-2">
          <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
          <span className="text-sm text-red-700 font-medium flex-1">¿Eliminar este servicio?</span>
          <button type="button" onClick={onCancelarBorrar} className="px-3 py-1.5 bg-white text-gray-600 text-xs font-semibold rounded-lg border border-gray-300">
            No
          </button>
          <button type="button" onClick={onBorrar} className="px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg">
            Sí, eliminar
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 mt-auto">
          <button
            type="button"
            onClick={onEditar}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border marca-tarjeta marca-acento py-2 text-sm font-semibold"
          >
            <Pencil className="w-3.5 h-3.5" /> Editar
          </button>
          <button
            type="button"
            onClick={onPedirBorrar}
            className="p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
            aria-label={`Eliminar ${service.name}`}
            title="Eliminar"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────
// FORMULARIO DE UN SERVICIO (hoja en el celular, tarjeta en la computadora)
// ─────────────────────────────────────
interface FormularioServicioProps {
  inicial: ServiceFormData;
  userId: number;
  tipo: 'salud' | 'belleza';
  guardando: boolean;
  onGuardar: (data: ServiceFormData, ventanas?: ServiceScheduleWindow[]) => void;
  onCerrar: () => void;
}

const FormularioServicio: React.FC<FormularioServicioProps> = ({ inicial, userId, tipo, guardando, onGuardar, onCerrar }) => {
  const [formData, setFormData] = useState<ServiceFormData>(inicial);
  const [showAdvanced, setShowAdvanced] = useState(
    Boolean(inicial.before_care_instructions || inicial.after_care_instructions)
  );
  const [intento, setIntento] = useState(false);

  // Horarios específicos del servicio
  const [useCustomSchedule, setUseCustomSchedule] = useState(false);
  const [scheduleWindows, setScheduleWindows] = useState<ServiceScheduleWindow[]>([]);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  // Horario de atención del profesional (para avisar si una franja se sale)
  const [workingDays, setWorkingDays] = useState<DiaDeAtencion[]>([]);
  // Sedes activas. Con más de una, cada franja elige en qué sede vale
  // (migración 029) y se compara contra el horario de ESA sede: antes se
  // comparaba contra la sede principal, y a Gallo eso le hacía marcar
  // "habilitar este día" en días que sí atiende, pero en otra sede.
  const [sedes, setSedes] = useState<Array<{ id: number; name: string }>>([]);
  const [diasPorSede, setDiasPorSede] = useState<Record<number, DiaDeAtencion[]>>({});

  // Sin scroll de la página de atrás mientras está abierto
  useEffect(() => {
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previo; };
  }, []);

  // Horario contra el que se compara una franja: el de su sede; si vale en
  // todas, un día cuenta como de atención si alguna sede atiende ese día.
  // [] = todavía no cargó (no se avisa nada).
  const diasDe = (w: ServiceScheduleWindow): DiaDeAtencion[] => {
    if (sedes.length < 2) return workingDays;
    if (sedes.some(s => !diasPorSede[s.id])) return [];
    if (w.locationId) return diasPorSede[w.locationId] || [];
    return dayKeys.map((key) => {
      const deEseDia = sedes
        .map(s => diasPorSede[s.id].find(d => d.day === key))
        .filter((d): d is DiaDeAtencion => !!d && d.isWorking);
      if (deEseDia.length === 0) return { day: key, isWorking: false, startTime: '', endTime: '' };
      return {
        day: key,
        isWorking: true,
        startTime: deEseDia.map(d => d.startTime).sort()[0],
        endTime: deEseDia.map(d => d.endTime).sort()[deEseDia.length - 1],
      };
    });
  };

  // Nombre de la sede de la franja (null = vale en todas), para los avisos
  const nombreSede = (w: ServiceScheduleWindow): string | null =>
    (w.locationId && sedes.find(s => s.id === w.locationId)?.name) || null;

  // ¿Esta franja cae en un día que NO trabajás según tu horario general?
  // En ese caso no alcanza con cargarla: hay que marcar el opt-in, porque por
  // defecto la franja solo RECORTA días que ya atendés.
  const isNonWorkingDay = (w: ServiceScheduleWindow): boolean => {
    const dias = diasDe(w);
    if (!dias.length) return false; // todavía no cargó
    const wd = dias.find(d => d.day === dayKeys[w.dayOfWeek]);
    return !wd || !wd.isWorking;
  };

  // Aviso (no bloqueante) si la franja cae fuera del horario de atención de ese día
  const warningFor = (w: ServiceScheduleWindow): string | null => {
    const dias = diasDe(w);
    if (!dias.length) return null; // todavía no cargó
    const wd = dias.find(d => d.day === dayKeys[w.dayOfWeek]);
    if (!wd || !wd.isWorking) return null; // lo resuelve el checkbox de abajo
    const start = (wd.startTime || '').substring(0, 5);
    const end = (wd.endTime || '').substring(0, 5);
    if (start && end && (w.startTime < start || w.endTime > end)) {
      // "Solo en días especiales": un día especial reemplaza el horario de
      // ese día, y ahí la franja sí da turnos (los EEG de la técnica de Gallo).
      return `Tu horario semanal los ${dayLabels[w.dayOfWeek]}${nombreSede(w) ? ` en ${nombreSede(w)}` : ''} es ${start}–${end}. Fuera de ese horario, esta franja solo da turnos en los días especiales que cargues.`;
    }
    return null;
  };

  // Al editar un servicio existente, cargar sus horarios custom (si tiene)
  useEffect(() => {
    if (!inicial.id) return;
    apiService.getServiceSchedules(userId, inicial.id)
      .then(res => {
        if (res.schedules.length > 0) {
          setUseCustomSchedule(true);
          setScheduleWindows(res.schedules.map(s => ({ ...s, locationId: s.locationId ?? null })));
        }
      })
      .catch(() => { /* sin horarios custom, ignora */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar el horario de atención para poder avisar si una franja se sale
  useEffect(() => {
    apiService.getScheduleConfig(userId)
      .then(res => setWorkingDays(res.scheduleConfig?.workingDays || []))
      .catch(() => { /* sin config, no avisamos */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sedes activas y el horario de cada una (solo hace falta con 2 o más)
  useEffect(() => {
    apiService.getLocations(userId)
      .then(async (res) => {
        const activas = (res.locations || []).filter(l => l.is_active).map(l => ({ id: l.id, name: l.name }));
        setSedes(activas);
        if (activas.length < 2) return;
        const pares = await Promise.all(activas.map(async (s) => {
          const r = await apiService.getScheduleConfig(userId, s.id).catch(() => null);
          return [s.id, r?.scheduleConfig?.workingDays || []] as const;
        }));
        setDiasPorSede(Object.fromEntries(pares));
      })
      .catch(() => { /* sin sedes: se compara contra el horario general */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addWindow = () => {
    setScheduleWindows(prev => [...prev, { dayOfWeek: 1, startTime: '09:00', endTime: '12:00', opensNonWorkingDay: false, locationId: null }]);
  };

  const removeWindow = (idx: number) => {
    setScheduleWindows(prev => prev.filter((_, i) => i !== idx));
  };

  const updateWindow = (idx: number, field: keyof ServiceScheduleWindow, value: any) => {
    setScheduleWindows(prev => prev.map((w, i) => i === idx ? { ...w, [field]: value } : w));
  };

  const updateField = (field: keyof ServiceFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const faltaNombre = !formData.name.trim();
  const faltaCategoria = !formData.category;
  const faltaDuracion = !formData.duration || formData.duration < 5;
  const faltaPrecio = formData.price_mode === 'fijo' && !(Number(formData.price) > 0);

  const handleSubmit = () => {
    setIntento(true);
    setScheduleError(null);
    if (faltaNombre || faltaCategoria || faltaDuracion || faltaPrecio) return;

    if (useCustomSchedule) {
      if (scheduleWindows.length === 0) {
        setScheduleError('Agregá al menos una franja horaria o apagá el horario propio.');
        return;
      }
      for (const w of scheduleWindows) {
        if (w.startTime >= w.endTime) {
          setScheduleError(`En ${dayLabels[w.dayOfWeek]}, la hora de inicio tiene que ser anterior a la de fin.`);
          return;
        }
      }
    }

    // Si el horario propio está apagado, se manda [] para limpiar las franjas.
    const finalWindows = useCustomSchedule ? scheduleWindows : [];
    onGuardar({ ...formData, name: formData.name.trim() }, finalWindows);
  };

  // Las categorías del rubro; si el servicio tiene otra (cargada antes), se suma
  const categorias = formData.category && !CATEGORIAS[tipo].includes(formData.category)
    ? [...CATEGORIAS[tipo], formData.category]
    : CATEGORIAS[tipo];
  // Obra social es de salud; en belleza solo aparece si el servicio ya la tenía
  const modos: [PriceMode, string][] = [
    ['fijo', 'Precio fijo'],
    ...(tipo === 'salud' || formData.price_mode === 'obra_social' ? [['obra_social', 'Obra social'] as [PriceMode, string]] : []),
    ['consultar', 'A consultar'],
  ];
  const quienes = tipo === 'salud' ? 'tus pacientes' : 'tus clientes';
  const etiqueta = 'block text-sm font-semibold text-gray-800 mb-2';
  const campo = 'marca-campo w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base sm:text-sm text-gray-800';

  return createPortal(
    <div className={`marca marca--${tipo} fixed inset-0 z-[9999] sm:flex sm:items-center sm:justify-center sm:p-4`}>
      <div className="absolute inset-0 bg-slate-900/55 animate-backdrop-in" onClick={() => { if (!guardando) onCerrar(); }} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="servicio-titulo"
        className="absolute inset-0 sm:relative sm:inset-auto sm:w-full sm:max-w-2xl sm:max-h-[92vh] sm:rounded-3xl bg-white shadow-2xl flex flex-col overflow-hidden text-left animate-slide-up"
      >
        {/* Encabezado en arco */}
        <div className="marca-portada relative shrink-0 px-5 pb-9 text-white" style={{ paddingTop: 'max(14px, env(safe-area-inset-top))' }}>
          <span className="marca-mancha marca-mancha--1" />
          <span className="marca-mancha marca-mancha--2" />
          <div className="relative flex items-start justify-between gap-3">
            <div>
              <h2 id="servicio-titulo" className="text-2xl font-bold leading-tight">
                {formData.id ? 'Editar servicio' : 'Nuevo servicio'}
              </h2>
              <p className="mt-1 text-sm text-white/90">Lo que ven {quienes} al reservar.</p>
            </div>
            <button
              type="button"
              onClick={onCerrar}
              disabled={guardando}
              className="p-2 -mr-2 rounded-xl hover:bg-white/15 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 pt-3 pb-6 space-y-5">
          <div>
            <label htmlFor="servicio-nombre" className={etiqueta}>Nombre del servicio</label>
            <input
              id="servicio-nombre"
              type="text"
              value={formData.name}
              onChange={(e) => updateField('name', e.target.value)}
              className={campo}
              placeholder={tipo === 'salud' ? 'Ej: Consulta, Control...' : 'Ej: Lifting de pestañas...'}
            />
            {intento && faltaNombre && <p className="mt-1.5 text-xs text-red-600">Poné un nombre.</p>}
          </div>

          <div>
            <label htmlFor="servicio-descripcion" className={etiqueta}>Descripción <span className="font-normal text-gray-400">(opcional)</span></label>
            <textarea
              id="servicio-descripcion"
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              rows={2}
              className={`${campo} resize-none`}
              placeholder="Qué incluye, cómo prepararse..."
            />
          </div>

          <div>
            <p className={etiqueta}>Duración</p>
            <GrupoDeFichas
              etiqueta="Duración"
              opciones={DURACIONES.map(m => [m, formatDuration(m)] as [number, string])}
              valor={formData.duration}
              onCambio={(m) => updateField('duration', m)}
            />
            <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
              <span>Otra:</span>
              <input
                type="text"
                inputMode="numeric"
                aria-label="Otra duración en minutos"
                value={DURACIONES.includes(formData.duration) ? '' : String(formData.duration || '')}
                onChange={(e) => updateField('duration', Number(e.target.value.replace(/\D/g, '').slice(0, 3)) || 0)}
                placeholder="—"
                className="marca-campo w-20 rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
              />
              <span>min</span>
            </div>
            {intento && faltaDuracion && <p className="mt-1.5 text-xs text-red-600">Elegí cuánto dura (5 minutos o más).</p>}
          </div>

          <div>
            <p className={etiqueta}>Precio</p>
            <GrupoDeFichas
              etiqueta="Cómo se cobra"
              opciones={modos}
              valor={formData.price_mode}
              onCambio={(m) => updateField('price_mode', m)}
            />
            {formData.price_mode === 'fijo' ? (
              <div className="relative mt-3">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                {/* Texto y no número: "25.000" en un input numérico quedaba en 25 */}
                <input
                  type="text"
                  inputMode="numeric"
                  aria-label="Precio en pesos"
                  value={conPuntos(Number(formData.price) || 0)}
                  onChange={(e) => updateField('price', Number(e.target.value.replace(/\D/g, '').slice(0, 9)) || 0)}
                  placeholder="20.000"
                  className={`${campo} pl-8`}
                />
              </div>
            ) : (
              <p className="mt-2 text-xs leading-snug text-gray-500">
                En tu página va a decir <strong>{formData.price_mode === 'obra_social' ? '"Obra social"' : '"A consultar"'}</strong> en
                lugar del precio, y este servicio no pasa por el cobro online.
              </p>
            )}
            {intento && faltaPrecio && <p className="mt-1.5 text-xs text-red-600">Poné el precio, o elegí "A consultar".</p>}
          </div>

          <div>
            <p className={etiqueta}>Categoría</p>
            <GrupoDeFichas
              chicas
              etiqueta="Categoría"
              opciones={categorias.map(c => [c, c] as [string, string])}
              valor={formData.category}
              onCambio={(c) => updateField('category', c)}
            />
            {intento && faltaCategoria && <p className="mt-1.5 text-xs text-red-600">Elegí una categoría.</p>}
          </div>

          <div className="rounded-2xl border border-gray-200 p-4 space-y-4">
            <OpcionConInterruptor
              titulo="Visible para reservar"
              detalle={formData.is_active ? 'Aparece en tu página y se puede reservar.' : 'Oculto: no aparece en tu página.'}
              activo={formData.is_active}
              onCambio={(v) => updateField('is_active', v)}
            />
            <div className="border-t border-gray-100" />
            <OpcionConInterruptor
              titulo="Requiere consulta previa"
              detalle="Para servicios que se hacen después de una primera consulta."
              activo={formData.requires_consultation}
              onCambio={(v) => updateField('requires_consultation', v)}
            />
          </div>

          {/* Indicaciones antes y después (plegable) */}
          <div className="rounded-2xl border border-gray-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full flex items-center justify-between px-4 py-3 text-left"
              aria-expanded={showAdvanced}
            >
              <span>
                <span className="block text-sm font-semibold text-gray-900">Indicaciones</span>
                <span className="block text-xs text-gray-500">Qué hacer antes y después del servicio</span>
              </span>
              {showAdvanced ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
            </button>
            {showAdvanced && (
              <div className="px-4 pb-4 space-y-4">
                <div>
                  <label htmlFor="servicio-antes" className="block text-xs font-semibold text-gray-600 mb-1.5">Antes</label>
                  <textarea
                    id="servicio-antes"
                    value={formData.before_care_instructions}
                    onChange={(e) => updateField('before_care_instructions', e.target.value)}
                    rows={2}
                    className={`${campo} resize-none`}
                    placeholder="Ej: Venir sin maquillaje en los ojos..."
                  />
                </div>
                <div>
                  <label htmlFor="servicio-despues" className="block text-xs font-semibold text-gray-600 mb-1.5">Después</label>
                  <textarea
                    id="servicio-despues"
                    value={formData.after_care_instructions}
                    onChange={(e) => updateField('after_care_instructions', e.target.value)}
                    rows={2}
                    className={`${campo} resize-none`}
                    placeholder="Ej: No mojar la zona por 24 horas..."
                  />
                </div>
              </div>
            )}
          </div>

          {/* Horario propio del servicio (opcional) */}
          <div className="rounded-2xl border border-gray-200 p-4">
            <OpcionConInterruptor
              titulo="Horario propio"
              detalle="Este servicio solo se puede reservar en las franjas que elijas, dentro de tu horario de atención."
              activo={useCustomSchedule}
              onCambio={setUseCustomSchedule}
            >
              <div className="space-y-3">
                {sedes.length > 1 && (
                  <p className="text-xs text-gray-500">Como atendés en varias sedes, cada franja puede valer en todas o solo en una.</p>
                )}

                {scheduleWindows.map((w, idx) => {
                  const warn = warningFor(w);
                  return (
                    <div key={idx} className="rounded-2xl border marca-tarjeta p-3 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <select
                          value={w.dayOfWeek}
                          onChange={(e) => updateWindow(idx, 'dayOfWeek', parseInt(e.target.value))}
                          aria-label="Día de la franja"
                          className="marca-campo px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white flex-1 min-w-[110px]"
                        >
                          {dayLabels.map((label, dow) => (
                            <option key={dow} value={dow}>{label}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => removeWindow(idx)}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Quitar franja"
                          aria-label="Quitar franja"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="time"
                          value={w.startTime}
                          onChange={(e) => updateWindow(idx, 'startTime', e.target.value)}
                          aria-label="Desde"
                          className="marca-campo px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white"
                        />
                        <span className="text-gray-500 text-sm">a</span>
                        <input
                          type="time"
                          value={w.endTime}
                          onChange={(e) => updateWindow(idx, 'endTime', e.target.value)}
                          aria-label="Hasta"
                          className="marca-campo px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white"
                        />
                      </div>
                      {sedes.length > 1 && (
                        <select
                          value={w.locationId ?? ''}
                          onChange={(e) => updateWindow(idx, 'locationId', e.target.value ? Number(e.target.value) : null)}
                          className="marca-campo w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white"
                          aria-label="Sede donde vale esta franja"
                        >
                          <option value="">Todas las sedes</option>
                          {sedes.map(s => (
                            <option key={s.id} value={s.id}>Solo en {s.name}</option>
                          ))}
                        </select>
                      )}
                      {warn && (
                        <p className="text-[11px] text-amber-700 flex items-start gap-1">
                          <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                          <span>{warn}</span>
                        </p>
                      )}
                      {isNonWorkingDay(w) && (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!w.opensNonWorkingDay}
                              onChange={(e) => updateWindow(idx, 'opensNonWorkingDay', e.target.checked)}
                              className="mt-0.5 w-4 h-4 rounded border-amber-300 shrink-0"
                            />
                            <span className="text-[11px] leading-snug text-amber-800">
                              {nombreSede(w)
                                ? <>En {nombreSede(w)} no atendés los <strong>{dayLabels[w.dayOfWeek]}</strong>.</>
                                : sedes.length > 1
                                  ? <>Ninguna de tus sedes atiende los <strong>{dayLabels[w.dayOfWeek]}</strong>.</>
                                  : <>En tu horario general no atendés los <strong>{dayLabels[w.dayOfWeek]}</strong>.</>}
                              {' '}Marcá esta casilla para <strong>habilitar ese día
                                {nombreSede(w) ? ` en ${nombreSede(w)}` : sedes.length > 1 ? ' en todas tus sedes' : ''},
                                solo para este servicio</strong>.
                              {/* No decir "no genera turnos": en un día especial de ese
                                  día de la semana sí genera, y creer lo contrario llevó a
                                  Gallo a marcar la casilla y abrir lunes y sábados en
                                  todas sus sedes (sep-2026). */}
                              {!w.opensNonWorkingDay && (
                                <span className="block mt-0.5 text-amber-700/80">
                                  Sin marcar, esta franja solo da turnos en los días especiales que cargues.
                                </span>
                              )}
                            </span>
                          </label>
                        </div>
                      )}
                    </div>
                  );
                })}

                <button
                  type="button"
                  onClick={addWindow}
                  className="marca-acento inline-flex items-center gap-1 text-sm font-semibold"
                >
                  <Plus className="w-4 h-4" /> Agregar franja
                </button>

                {scheduleError && (
                  <p className="flex items-center gap-1.5 text-xs text-red-600">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {scheduleError}
                  </p>
                )}
              </div>
            </OpcionConInterruptor>
          </div>
        </div>

        {/* Pie */}
        <div
          className="shrink-0 border-t border-gray-100 bg-white px-5 pt-3 flex items-center gap-3"
          style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={onCerrar}
            disabled={guardando}
            className="px-4 py-3 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={guardando}
            className="marca-boton flex-1 flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-base font-semibold shadow-sm"
          >
            {guardando ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Guardando...
              </>
            ) : (
              formData.id ? 'Guardar cambios' : 'Crear servicio'
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─────────────────────────────────────
// PANTALLA
// ─────────────────────────────────────
const ProfessionalServicesConfig: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [editingService, setEditingService] = useState<ServiceFormData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const tipo: 'salud' | 'belleza' = user?.professional_type === 'belleza' ? 'belleza' : 'salud';
  const quienes = tipo === 'salud' ? 'tus pacientes' : 'tus clientes';

  // Verificar usuario y cargar datos
  useEffect(() => {
    const userData = localStorage.getItem('user');
    const loginStatus = localStorage.getItem('isLoggedIn');

    if (!userData || loginStatus !== 'true') {
      navigate('/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);

      if (!parsedUser.es_profesional) {
        navigate('/');
        return;
      }

      if (!parsedUser.professional_type ||
          !['salud', 'belleza'].includes(parsedUser.professional_type)) {
        parsedUser.professional_type = 'belleza';
      }

      setUser(parsedUser);
      loadServices(parsedUser.id);
    } catch (err) {
      console.error('Error parsing user data:', err);
      navigate('/login');
    }
  }, [navigate]);

  const loadServices = async (professionalId: number) => {
    try {
      const response = await apiService.getProfessionalServices(professionalId);
      setServices(response.services);
    } catch (err: any) {
      setError('Error cargando servicios: ' + apiService.getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleSaveService = async (
    serviceData: ServiceFormData,
    scheduleWindows?: ServiceScheduleWindow[]
  ) => {
    if (!user) return;
    setSaving(true);
    setError(null);

    try {
      const backendData = {
        name: serviceData.name,
        description: serviceData.description,
        duration: serviceData.duration,
        price: serviceData.price,
        category: serviceData.category,
        requiresConsultation: serviceData.requires_consultation,
        beforeCareInstructions: serviceData.before_care_instructions,
        afterCareInstructions: serviceData.after_care_instructions,
        isActive: serviceData.is_active,
        priceMode: serviceData.price_mode || 'fijo'
      };

      let savedServiceId = serviceData.id;

      if (serviceData.id) {
        await apiService.updateService(user.id, serviceData.id, backendData);
        showSuccess('Servicio actualizado');
      } else {
        const resp = await apiService.createService(user.id, backendData);
        savedServiceId = resp.service?.id;
        showSuccess('Servicio creado');
      }

      // Guardar horarios específicos del servicio (reemplaza todos).
      // scheduleWindows = [] limpia los horarios custom → usa horario general.
      if (savedServiceId && scheduleWindows !== undefined) {
        await apiService.saveServiceSchedules(user.id, savedServiceId, scheduleWindows);
      }

      await loadServices(user.id);
      setEditingService(null);
    } catch (err: any) {
      // El formulario queda abierto y con lo que se escribió
      setError('Error guardando el servicio: ' + apiService.getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteService = async (serviceId: number) => {
    if (!user) return;
    try {
      await apiService.deleteService(user.id, serviceId);
      await loadServices(user.id);
      setDeleteConfirm(null);
      showSuccess('Servicio eliminado');
    } catch (err: any) {
      setError('Error eliminando el servicio: ' + apiService.getErrorMessage(err));
      setDeleteConfirm(null);
    }
  };

  const openEdit = (service: Service) => {
    setEditingService({
      id: service.id,
      name: service.name,
      description: service.description || '',
      duration: service.duration,
      price: Number(service.price) || 0,
      price_mode: service.price_mode || 'fijo',
      category: service.category,
      requires_consultation: service.requires_consultation,
      before_care_instructions: service.before_care_instructions || '',
      after_care_instructions: service.after_care_instructions || '',
      is_active: service.is_active
    });
  };

  const openCreate = () => {
    if (!user) return;
    setEditingService({ ...FORMULARIO_VACIO, category: CATEGORIAS[tipo][0] });
  };

  // Separar activos e inactivos
  const activeServices = services.filter(s => s.is_active);
  const inactiveServices = services.filter(s => !s.is_active);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <LoadingSpinner size={48} label="Cargando servicios..." />
      </div>
    );
  }

  const tarjetaDe = (service: Service, i: number) => (
    <TarjetaServicio
      key={service.id}
      service={service}
      tipo={tipo}
      orden={i}
      borrando={deleteConfirm === service.id}
      onEditar={() => openEdit(service)}
      onPedirBorrar={() => setDeleteConfirm(service.id)}
      onBorrar={() => handleDeleteService(service.id)}
      onCancelarBorrar={() => setDeleteConfirm(null)}
    />
  );

  return (
    <div className={`marca marca--${tipo} min-h-screen bg-slate-50 text-left`}>
      <PortadaMarca
        titulo="Servicios"
        bajada={`Lo que te pueden reservar ${quienes}.`}
        onVolver={() => navigate(-1)}
        ancho="max-w-5xl"
        accion={
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-1.5 rounded-xl bg-white px-3.5 py-2 text-sm font-semibold marca-acento shadow-sm hover:bg-white/90"
          >
            <Plus className="w-4 h-4" /> Nuevo servicio
          </button>
        }
      >
        {services.length > 0 && (
          <div className="flex flex-wrap gap-2 text-xs font-semibold">
            <span className="rounded-full bg-white/20 px-3 py-1">
              {activeServices.length} {activeServices.length === 1 ? 'activo' : 'activos'}
            </span>
            {inactiveServices.length > 0 && (
              <span className="rounded-full bg-white/20 px-3 py-1">
                {inactiveServices.length} {inactiveServices.length === 1 ? 'oculto' : 'ocultos'}
              </span>
            )}
          </div>
        )}
      </PortadaMarca>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-6 relative pb-32">
        {services.length === 0 ? (
          <div className="bg-white rounded-3xl border border-gray-200 p-8 sm:p-12 text-center shadow-sm">
            <div className="w-16 h-16 rounded-2xl border marca-tarjeta marca-acento flex items-center justify-center mx-auto mb-5">
              {tipo === 'belleza' ? <Sparkles className="w-8 h-8" /> : <Stethoscope className="w-8 h-8" />}
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Todavía no tenés servicios</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Sumá lo que atendés para que {quienes} puedan reservar desde tu link.
            </p>
            <button
              type="button"
              onClick={openCreate}
              className="marca-boton inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold"
            >
              <Plus className="w-5 h-5" /> Crear mi primer servicio
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeServices.map((service, i) => tarjetaDe(service, i))}
            </div>

            {inactiveServices.length > 0 && (
              <div className="mt-8">
                <button
                  type="button"
                  onClick={() => setShowInactive(!showInactive)}
                  className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors mb-4"
                >
                  {showInactive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {showInactive ? 'Ocultar' : 'Mostrar'} servicios ocultos ({inactiveServices.length})
                  {showInactive ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showInactive && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {inactiveServices.map((service, i) => tarjetaDe(service, i))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Avisos: arriba, debajo del encabezado del sitio (y encima del
          formulario, que es donde se ve un error al guardar) */}
      {(error || successMessage) && createPortal(
        <div className="fixed inset-x-0 top-20 z-[10000] px-4 pointer-events-none">
          <div
            className={`max-w-md mx-auto pointer-events-auto flex items-start gap-2 rounded-2xl border px-4 py-3 text-sm shadow-lg animate-slide-up ${
              error ? 'bg-red-50 border-red-200 text-red-800' : 'bg-white border-gray-200 text-gray-800'
            }`}
            role={error ? 'alert' : 'status'}
          >
            {error ? <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> : <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-green-600" />}
            <span className="flex-1">{error || successMessage}</span>
            <button type="button" onClick={() => { setError(null); setSuccessMessage(null); }} className="opacity-60 hover:opacity-100" aria-label="Cerrar aviso">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>,
        document.body
      )}

      {editingService && user && (
        <FormularioServicio
          inicial={editingService}
          userId={user.id}
          tipo={tipo}
          guardando={saving}
          onGuardar={handleSaveService}
          onCerrar={() => { if (!saving) setEditingService(null); }}
        />
      )}
    </div>
  );
};

export default ProfessionalServicesConfig;
